import { getDayjsOrNull } from '@payler/utils';
import dayjs, { Dayjs } from 'dayjs';
import { TFunction } from 'i18next';

export const MAX_PERIOD_DAYS = 32;

type TEnvironment = 'prod' | 'dev' | 'sandbox' | 'test' | 'te30' | 'neo' | 'main' | 'sand' | 'common-gate-dev' | 'common-gate-test' | 'common-gate-loadtest' | 'payler-gate-prod' | 'payler-gate-sandbox' | undefined;

const isProd = (environment: TEnvironment): boolean => environment === 'prod';

const isEmptyValue = (value: any): boolean => 
  !value || value === '' || String(value).trim() === '';

const getDateRange = (date1: Dayjs, date2: Dayjs): [Dayjs, Dayjs] => {
  const [d1, d2] = [date1.startOf('day'), date2.startOf('day')];
  return d1.isBefore(d2) ? [d1, d2] : [d2, d1];
};

const getInclusiveDays = (start: Dayjs, end: Dayjs): number => {
  const daysDiff = end.diff(start, 'day');
  return daysDiff + 1;
};

const checkPeriod = (start: Dayjs, end: Dayjs, t: TFunction, ctx: any, isProd: boolean) => {
  if (!isProd) return true;
  const inclusiveDays = getInclusiveDays(start, end);
  if (inclusiveDays > MAX_PERIOD_DAYS) {
    return ctx.createError({
      message: t('common:datePicker.rangeError', 'The period cannot exceed 1 month'),
    });
  }
  return true;
};

export const createPeriodRangeValidator = (
  t: TFunction,
  dateFormat: string,
  otherFieldName: string,
  environment: TEnvironment,
  useTodayAsDefault: boolean = false,
) => {
  return (value: string | null | undefined, ctx: any) => {
    const prod = isProd(environment);
    const otherValue = ctx.parent[otherFieldName];
    const isToField = /To|to|End/i.test(otherFieldName);
    
    if (isEmptyValue(value)) {
      return true;
    }
    
    const date = getDayjsOrNull(value, dateFormat);
    if (!date?.isValid()) {
      return true;
    }
    
    if (!useTodayAsDefault) {
      if (isEmptyValue(otherValue)) {
        return true;
      }
      const date2 = getDayjsOrNull(otherValue, dateFormat);
      if (!date2?.isValid()) {
        return true;
      }
      const [start, end] = getDateRange(date, date2);
      return checkPeriod(start, end, t, ctx, prod);
    }
    
    if (isEmptyValue(otherValue)) {
      if (isToField) {
        const date2 = dayjs().startOf('day');
        const [start, end] = getDateRange(date, date2);
        return checkPeriod(start, end, t, ctx, prod);
      }
      return true;
    }
    
    const parsedDate2 = getDayjsOrNull(otherValue, dateFormat);
    if (parsedDate2?.isValid()) {
      const [start, end] = getDateRange(date, parsedDate2);
      return checkPeriod(start, end, t, ctx, prod);
    }
    
    return true;
  };
};

export const validatePeriodMaxLength = (
  value: string | null | undefined,
  otherFieldValue: string | null | undefined,
  dateFormat: string,
  environment: TEnvironment,
): boolean => {
  if (!isProd(environment) || !value || !otherFieldValue) return true;
  
  const start = getDayjsOrNull(value, dateFormat);
  const end = getDayjsOrNull(otherFieldValue, dateFormat);
  
  if (!start || !end) return true;
  
  return getInclusiveDays(start, end) <= MAX_PERIOD_DAYS;
};

export const normalizePeriodFilters = <T extends { StartDate?: string | null; EndDate?: string | null } | { startDate?: string | null; endDate?: string | null }>(
  filters: T,
  dateFormat: string,
  environment: TEnvironment,
): T => {
  if (!isProd(environment)) return filters;

  const hasStartDate = 'StartDate' in filters;
  const startDateKey = hasStartDate ? 'StartDate' : 'startDate';
  const endDateKey = hasStartDate ? 'EndDate' : 'endDate';
  const startDate = filters[startDateKey as keyof T] as string | null | undefined;
  const endDate = filters[endDateKey as keyof T] as string | null | undefined;

  if (!startDate || !endDate) {
      return {
        ...filters,
      [startDateKey]: startDate || dayjs().subtract(1, 'day').format(dateFormat),
      [endDateKey]: endDate || dayjs().format(dateFormat),
      } as T;
  }

  const start = getDayjsOrNull(startDate, dateFormat);
  const end = getDayjsOrNull(endDate, dateFormat);
  
  if (!start || !end) return filters;
  
  if (getInclusiveDays(start, end) > MAX_PERIOD_DAYS) {
      return {
        ...filters,
      [endDateKey]: start.add(MAX_PERIOD_DAYS - 1, 'day').format(dateFormat),
      } as T;
  }

  return filters;
};

